import { useEffect, useState } from 'react';
import axios from 'axios';
import { Hash, Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LOT B — Paramétrage (admin) de la référence externe d'une société cliente.
 * Libellé libre : « N° dossier transit », « N° de commande », « Code chantier »...
 */
export const ExternalReferenceConfigCard = ({ companyName }) => {
  const [form, setForm] = useState({ enabled: false, label: '', required: false, helpText: '', clientCanEdit: true, groupingEnabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/external-reference/config?company=${encodeURIComponent(companyName)}`);
        if (alive) setForm({
          enabled: !!res.data?.enabled,
          label: res.data?.label || '',
          required: !!res.data?.required,
          helpText: res.data?.helpText || '',
          clientCanEdit: res.data?.clientCanEdit !== false,
          groupingEnabled: !!res.data?.groupingEnabled,
        });
      } catch { /* config par défaut */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [companyName]);

  const save = async () => {
    if (form.enabled && !form.label.trim()) {
      toast.error('Libellé requis', { description: 'Indiquez le libellé affiché au client (ex. « N° dossier transit »).' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/external-reference/config/${encodeURIComponent(companyName)}`, form);
      toast.success('Référence externe enregistrée', {
        description: form.enabled ? `Champ « ${form.label} » actif pour ${companyName}` : 'Champ désactivé pour ce client',
      });
    } catch (e) {
      toast.error('Erreur', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="border rounded-lg p-4 text-xs text-slate-400">Chargement de la référence externe…</div>;
  }

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-slate-50/60" data-testid="external-ref-config-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Hash className="w-4 h-4 mt-0.5 text-[#1db584]" strokeWidth={1.75} />
          <div>
            <p className="text-sm font-semibold text-slate-800">Référence externe client</p>
            <p className="text-xs text-slate-500 max-w-md">
              Champ libre affiché au client et repris dans les listes, la recherche et les exports
              (ex. « N° dossier transit », « N° de commande », « Code chantier »).
            </p>
          </div>
        </div>
        <Switch
          checked={form.enabled}
          onCheckedChange={(v) => setForm({ ...form, enabled: v })}
          data-testid="external-ref-enabled-switch"
        />
      </div>

      {form.enabled && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
          <div>
            <Label className="text-xs">Libellé affiché *</Label>
            <Input
              value={form.label}
              maxLength={40}
              placeholder="N° dossier transit"
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              data-testid="external-ref-label-input"
            />
          </div>
          <div>
            <Label className="text-xs">Aide à la saisie</Label>
            <Input
              value={form.helpText}
              maxLength={80}
              placeholder="Format : D-2026-XXXXX"
              onChange={(e) => setForm({ ...form, helpText: e.target.value })}
              data-testid="external-ref-help-input"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.required}
              onChange={(e) => setForm({ ...form, required: e.target.checked })}
              data-testid="external-ref-required-checkbox"
            />
            Obligatoire (bloque la création et la facturation si absent)
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.clientCanEdit}
              onChange={(e) => setForm({ ...form, clientCanEdit: e.target.checked })}
              data-testid="external-ref-clientedit-checkbox"
            />
            Le client peut le saisir lui-même
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer md:col-span-2">
            <input
              type="checkbox"
              checked={form.groupingEnabled}
              onChange={(e) => setForm({ ...form, groupingEnabled: e.target.checked })}
              data-testid="external-ref-grouping-checkbox"
            />
            Activer la vue « par dossier » (regroupe les opérations d'une même référence avec sous-totaux facturé / reste à payer)
          </label>
        </div>
      )}

      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} data-testid="external-ref-save-btn"
          className="bg-[#1db584] hover:bg-[#16a075] text-white">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Enregistrer la référence
        </Button>
      </div>
    </div>
  );
};

export default ExternalReferenceConfigCard;
